/**
 * Voyage Radar credit-wallet primitives for the collections viewer.
 *
 * One balance (`apiCreditsRemaining` of `apiCredits`) backs every spendable
 * action; this file is the ONLY place that reads those fields, so the
 * null-vs-zero contract lives in exactly one function.
 */

import { canAfford, voyageFallbackCredits } from 'src/app/services/operators/voyageradar-credit-actions';

/**
 * Credits left on a sensor, or NULL when the wallet is unknown.
 *
 * `apiCreditsRemaining` is the running balance; it is written eagerly at task
 * creation, but a sensor from before that write (or from a partial payload)
 * falls back to the purchase — and a balance of exactly 0 must win over that
 * fallback, or an exhausted wallet would read as full.
 *
 * Two things here are deliberate (carried over from the counter-pair era —
 * both were wrong in the first version of this logic):
 *
 * `== 0` and `||`, not `===` and `??`, because the fallback write path still
 * computes `apiCredits - cost`, which yields NaN when the entitlement is
 * absent. `||` falls past NaN to the fallback; `??` does not, because NaN is
 * not nullish.
 *
 * NULL rather than 0 for "no usable number", because absent is not empty.
 * Returning 0 told the user their credits were exhausted on a sensor that had
 * simply never reported a wallet.
 */
export function voyageCreditsRemaining(sensor: any): number | null {
  if (!sensor) return null;
  // eslint-disable-next-line eqeqeq
  const raw = sensor.apiCreditsRemaining == 0
    ? 0
    : sensor.apiCreditsRemaining || sensor.apiCredits;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : null;
}

/**
 * The scene every Voyage Radar guard reads — the checked one, else the first.
 *
 * The same resolution `VoyageMapController.refreshSensor` performs. It lives
 * here, next to the two guards that call it, because a second copy is how one
 * control ends up reporting a TOI state for a scene the next control never
 * looked at — which is exactly what had happened: `hasVoyageToiFinished` had
 * inlined its own.
 */
export function voyageSensorScene(sensor: any): any {
  const scenes = sensor?.scenes ?? [];
  return scenes.find((s: any) => s?.checked) ?? scenes[0] ?? null;
}

/**
 * Has the task's time of interest ended?
 *
 * ONE fact with two consequences, which is why it is one function: the refund
 * window OPENS here, and every paid action CLOSES here. Once the TOI is over
 * the operator has nothing further to sell against this task, so the only thing
 * left to do with an unspent wallet is refund it — see `voyageSpendRefusal`,
 * which is the single gate every spend passes through.
 *
 * THE TIMESTAMP WINS OVER THE FLAGS, which is a reversal. Both flags are
 * stamped when the collection is fetched and never move again: `timeExpired` by
 * the server (tasks.route.js, from `closeWindow < now`) and `aisDownloadable`
 * by the client (`hasCloseWindowPassed`, from the same field). A session left
 * open across the boundary therefore keeps reading a flag that says the TOI is
 * still running — harmless when the answer only gated a refund button, but the
 * spend gate must not be wrong in that direction, and it is the direction a
 * stale flag is always wrong in.
 *
 * The flags remain the fallback for shapes that carry no timestamp: a sensor
 * with no scenes (the Days-task view-model builds one), or a partial payload.
 */
export function hasVoyageToiFinished(sensor: any, nowMs: number = Date.now()): boolean {
  const scene = voyageSensorScene(sensor);
  // Guarded rather than handed straight to `new Date`: a null closeWindow
  // parses to the epoch, which is finite and in the past — every task would
  // read as ended.
  const closeWindow = scene?.closeWindow ?? sensor?.closeWindow;
  const closeMs = closeWindow ? new Date(closeWindow).getTime() : NaN;
  if (Number.isFinite(closeMs)) return nowMs > closeMs;

  if (sensor?.timeExpired === true) return true;
  return (scene?.aisDownloadable ?? sensor?.aisDownloadable) === true;
}

/**
 * Why nothing on this task can be bought any more — the one sentence for it,
 * stated once, and the twin of `VOYAGE_TOI_NOT_STARTED_MESSAGE` at the other
 * end of the window.
 *
 * It names the refund because a refusal that only says "no" leaves the user
 * holding credits with no way to act on them, and the refund is the way.
 */
export const VOYAGE_TOI_ENDED_MESSAGE =
  'The time of interest (TOI) has ended, so credits can no longer be spent on ' +
  'this target. Any credits left can be refunded — use the refund icon on the ' +
  'sensor row. Please re-task if the target continues to be the same.';

/**
 * Whether there is a refund WORTH looking at: a non-zero balance and no
 * terminal/pending refund state already reached. This gates the popup itself
 * (and the dialog's request form) so usage stays viewable at any time — TOI
 * is deliberately NOT part of this check.
 */
export function canRequestVoyageRefund(sensor: any): boolean {
  const left = voyageCreditsRemaining(sensor);
  return (
    left != null &&
    left > 0 &&
    !sensor?.refundRequest &&
    !sensor?.returnRefundAccept &&
    !sensor?.returnRefundReject
  );
}

/**
 * Whether the refund request can actually be SUBMITTED — everything
 * `canRequestVoyageRefund` checks, plus the TOI having finished. Users may
 * apply for a refund only once the TOI is done.
 */
export function canSubmitVoyageRefund(sensor: any): boolean {
  return canRequestVoyageRefund(sensor) && hasVoyageToiFinished(sensor);
}

/**
 * Every paid Voyage Radar action, in the two voices an error needs: what the
 * user was trying to buy, and what to say when the attempt simply failed.
 *
 * Keyed by CATALOGUE KEY, so the thing the server priced and the thing the
 * message names are the same string — and a gesture therefore cannot be given
 * copy of its own: `voyageSpendErrorMessage` prices its last-resort fallback
 * from this same key, so an entry with no catalogue key behind it would be
 * free-form text that silently renders "0 credits".
 *
 * `refresh` and `nearby` once shared one label on purpose, because they were
 * separate priced actions behind a single user-facing gesture. That reason
 * expired when `nearby` became the price of an ENDPOINT rather than of one
 * gesture: it prices the point task's target refresh today, and it is the key
 * the per-vessel "who is around this hull" search will spend once plan steps
 * 4-8 build it (route, drawer, popup action — none of them started). Its
 * wording is shaped by that endpoint rather than by either gesture, so it has
 * to read correctly from both; `refresh` fronts only the area sweep and keeps
 * the gesture-shaped wording it always had.
 *
 * Only the first of those two gestures reaches this copy today: the tasked
 * refresh in leaflet-newcollections.component.ts, which asks `refreshActionKey`
 * for the key. No call site passes 'nearby' for a hull the user picked; this
 * file's own spec is the only place the key is written out by hand. So do not
 * read the label here as something a user can already do to a vessel they
 * selected — the drawer and popup action that would let them are unbuilt.
 */
const SPEND_ACTION_COPY: Record<string, { label: string; failure: string }> = {
  refresh: { label: 'a vessel refresh', failure: 'Failed to refresh vessel data.' },
  nearby: { label: 'a nearby-vessel search', failure: 'Failed to fetch nearby vessels.' },
  track: { label: 'a vessel track', failure: 'Failed to fetch the vessel track.' },
  search: { label: 'a worldwide search', failure: 'Failed to search for vessels.' },
  lookup: {
    label: 'the latest vessel details',
    failure: 'Failed to fetch the latest vessel details.',
  },
  portCalls: {
    label: 'a port-call history',
    failure: 'Failed to fetch the port-call history.',
  },
};

/** The catalogue keys that can be spent on from the map. */
export type VoyageSpendAction = keyof typeof SPEND_ACTION_COPY;

/**
 * Map a spend-endpoint error to user-facing copy. THE only place this is
 * written.
 *
 * It used to cover the refresh and track paths only, and the search, lookup and
 * port-call paths each grew their own inline three-branch ladder — four
 * implementations, two vocabularies, and the terse ones dropped the balance and
 * the way to fix it. One function, one voice, keyed by the same catalogue key
 * the server priced with.
 *
 * Order matters, and it is the same order `voyageSpendRefusal` runs on the way
 * out: the reasons no purchase can fix come first. `TOI_ENDED` is the window
 * having closed, and `OPERATOR_CREDITS_EXHAUSTED` is the PLATFORM's upstream
 * balance being dry — both can arrive alongside a 4xx/5xx status, and either
 * one reaching the wallet branch tells a user their own credits are spent and
 * sends them off to re-task for something re-tasking would not give them.
 *
 * EVERY branch states the charge outcome. Silence after a failed spend reads as
 * "it took my credit".
 */
export function voyageSpendErrorMessage(
  err: any,
  action: VoyageSpendAction,
  fallbackCost?: number,
): string {
  const copy = SPEND_ACTION_COPY[action] ?? {
    label: 'this action',
    failure: 'The request failed.',
  };
  const code = err?.error?.code;

  // The server's own TOI refusal, answered in the words the client already uses
  // for it. Without this branch a 403 fell to the bottom of the ladder and told
  // the user to "please try again" — advice for a call that can never succeed.
  if (code === 'TOI_ENDED') return VOYAGE_TOI_ENDED_MESSAGE;

  if (code === 'OPERATOR_CREDITS_EXHAUSTED') {
    // Never names the operator: users on obfuscated accounts must not learn it
    // from an error message.
    return (
      'This sensor is temporarily unable to serve new data. This is a ' +
      'platform-side issue, not your credit balance — no credits have been ' +
      'used. Please try again shortly or contact support.'
    );
  }

  if (err?.status === 402 || code === 'INSUFFICIENT_API_CREDITS') {
    // The SERVER's `requiredCredits` first — it priced the call that was just
    // refused, from the catalogue key it chose, so it is right about the target
    // shape even when the caller passed nothing. `fallbackCost` is the caller's
    // doc-resolved price; the shipped catalogue is the last resort.
    const cost =
      Number(err?.error?.requiredCredits) || fallbackCost || voyageFallbackCredits(action);
    const remaining = Number(err?.error?.apiCreditsRemaining);
    const remainingClause = Number.isFinite(remaining) ? ` You have ${remaining} remaining.` : '';
    return (
      `You do not have enough credits for ${copy.label} ` +
      `(${cost} ${cost === 1 ? 'credit' : 'credits'}).` +
      `${remainingClause} Re-task to buy more.`
    );
  }

  return `${copy.failure} You were not charged — please try again.`;
}

/**
 * The same refusal, made client-side before the request leaves.
 *
 * Deliberately routed through `voyageSpendErrorMessage` rather than written
 * again: a pre-flight refusal and the server's 402 are the SAME refusal decided
 * in two places, and a user who hits one and then the other must not be told
 * two different things about the same wallet.
 */
export function voyageInsufficientCreditsMessage(
  action: VoyageSpendAction,
  cost: number,
  remaining: number | null,
): string {
  return voyageSpendErrorMessage(
    {
      status: 402,
      error: {
        requiredCredits: cost,
        ...(remaining != null ? { apiCreditsRemaining: remaining } : {}),
      },
    },
    action,
  );
}

/**
 * What a spend gate needs to answer: the thing being bought, and the two facts
 * that decide whether it can be.
 *
 * The FACTS rather than a sensor, because half the callers do not have one — a
 * toolbar drawer is handed `{creditsRemaining, cost}` through `syncCreditLines`
 * and can reach neither the map nor the sensor. Callers that do hold one build
 * this with `voyageSensorSpendGate`.
 */
export interface VoyageSpendGate {
  action: VoyageSpendAction;
  cost: number;
  remaining: number | null;
  toiEnded: boolean;
}

/** `VoyageSpendGate` read off a sensor, for the callers that hold one. */
export function voyageSensorSpendGate(
  sensor: any,
  action: VoyageSpendAction,
  cost: number,
): VoyageSpendGate {
  return {
    action,
    cost,
    remaining: voyageCreditsRemaining(sensor),
    toiEnded: hasVoyageToiFinished(sensor),
  };
}

/**
 * THE client-side pre-flight gate for every paid Voyage Radar action: the
 * message to refuse with, or null when the spend may go ahead.
 *
 * One function because there is one order, and it has to hold everywhere: the
 * TOI closes the shop, and only inside an open shop does the balance matter.
 * Asking them the other way round tells a user with an empty wallet to "re-task
 * to buy more" for a task that can no longer spend what they would buy.
 *
 * Both refusals are the same sentences the SERVER's 403/402 produce, via
 * `voyageSpendErrorMessage` — a user who trips the client gate and then the
 * server one must not be told two different things about the same wallet.
 */
export function voyageSpendRefusal(gate: VoyageSpendGate): string | null {
  if (gate.toiEnded) return VOYAGE_TOI_ENDED_MESSAGE;
  if (!canAfford(gate.remaining, gate.cost)) {
    return voyageInsufficientCreditsMessage(gate.action, gate.cost, gate.remaining);
  }
  return null;
}

/**
 * The subset of `voyageSpendRefusal` a CONTROL should go dead for — everything
 * else stays pressable and is explained by the click.
 *
 * The two refusals are not the same kind of thing, and the drawers had drifted
 * into treating them as one:
 *
 *  - AN EMPTY WALLET is a standing condition of the control. It is true before
 *    the click, it stays true after it, and the cost line beside the button is
 *    already quoting the price and the balance the user would need — so a dead
 *    button with that line under it IS the whole explanation.
 *  - AN ENDED TOI is a fact about the TASK, not about this button, and its
 *    answer is a different act entirely (request a refund). A dead button
 *    cannot say that; a modal raised by the press can, and the press is what
 *    tells us the user wanted to know.
 *
 * So the TOI refusal deliberately does NOT block: the control stays live, the
 * click reaches the pre-flight in `VoyageMapController`, and that raises the
 * modal. This is the shape the trace drawer already had — every priced Voyage
 * Radar control now follows it, which is the point of stating the rule here
 * rather than in five templates.
 */
export function voyageSpendBlocker(gate: VoyageSpendGate): string | null {
  return gate.toiEnded ? null : voyageSpendRefusal(gate);
}

/**
 * If a spend error carries the server's balance, write it onto the sensor —
 * the client's number is by definition wrong at that moment, and re-syncing
 * before showing the popup is what stops the user clicking straight into a
 * second refusal.
 */
export function syncWalletFromError(err: any, sensor: any): void {
  const reported = Number(err?.error?.apiCreditsRemaining);
  if (sensor && Number.isFinite(reported)) {
    sensor.apiCreditsRemaining = Math.max(0, reported);
  }
}
